// SymmetryFunctionTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";

import EquationInput from "../../../../components/inputs/EquationInput";
import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";

/* =========================
   Helpers
========================= */

const randInt = (min, max, nonZero = false) => {
  let x;
  do {
    x = Math.floor(Math.random() * (max - min + 1)) + min;
  } while (nonZero && x === 0);
  return x;
};

// usuwa "g(x)=", "f(x)=", "y=" tylko na początku
const stripLeadingAssignment = (s) => {
  let t = String(s ?? "");
  t = t.replace(/−/g, "-").trim();
  t = t.replace(/^(g|f)\(x\)\s*=\s*/i, "");
  t = t.replace(/^y\s*=\s*/i, "");
  return t.trim();
};

const normalizeExpr = (v) => {
  const raw = typeof v === "object" ? v?.raw ?? "" : v ?? "";
  let s = String(raw);

  s = s.replace(/−/g, "-");
  s = s.replace(/\s+/g, "");
  s = s.replace(/^(g|f)\(x\)=/i, "");
  s = s.replace(/^y=/i, "");
  s = s.replace(/\*/g, "");

  for (let i = 0; i < 6; i++) {
    s = s.replace(/\+\+/g, "+");
    s = s.replace(/--/g, "+");
    s = s.replace(/\+-/g, "-");
    s = s.replace(/-\+/g, "-");
  }

  // ( +3 ) -> 3, ( -3 ) -> -3
  s = s.replace(/\(\+?(-?\d+)\)/g, "$1");

  // usuń zbędne nawiasy wokół -x: (-x) -> -x
  s = s.replace(/\(-x\)/g, "-x");
  return s;
};

// Latex wielomianu z współczynników stopnia 3..0 (zerowe pomija)
function polyLatex({ a3 = 0, a2 = 0, a1 = 0, a0 = 0 }) {
  const parts = [];

  const push = (coef, core) => {
    if (coef === 0) return;
    const sign = coef < 0 ? "-" : "+";
    const abs = Math.abs(coef);
    const coefStr = abs === 1 && core !== "" ? "" : String(abs);
    const term = core === "" ? `${abs}` : `${coefStr}${core}`;
    if (parts.length === 0) {
      parts.push(coef < 0 ? `-${term}` : `${term}`);
    } else {
      parts.push(`${sign} ${term}`);
    }
  };

  push(a3, "x^3");
  push(a2, "x^2");
  push(a1, "x");
  push(a0, "");

  return parts.join(" ").replace(/\s+/g, " ").trim() || "0";
}

function latexAx(a) {
  if (a === 1) return "x";
  if (a === -1) return "-x";
  return `${a}x`;
}

/** JS expr for GraphViewer equations */
function exprAx(a) {
  if (a === 1) return "x";
  if (a === -1) return "-x";
  return `${a} * x`;
}
function exprAxx(A) {
  if (A === 1) return "x * x";
  if (A === -1) return "-x * x";
  return `${A} * x * x`;
}
function exprAxxx(A) {
  if (A === 1) return "x * x * x";
  if (A === -1) return "-x * x * x";
  return `${A} * x * x * x`;
}

/* =========================
   Base function (same family)
========================= */

function makeBaseFunction() {
  // liniowa / kwadratowa / sześcienna (bez x^2)
  const r = Math.random();
  const kind = r < 0.4 ? "linear" : r < 0.8 ? "quadratic" : "cubic";

  if (kind === "linear") {
    let a = randInt(-5, 5, true);
    while (a === 1) a = randInt(-5, 5, true);
    const b = randInt(-7, 7, false);
    return { kind: "linear", a, b };
  }

  if (kind === "quadratic") {
    const A = randInt(-3, 3, true);
    const B = randInt(-6, 6, false);
    const C = randInt(-6, 6, false);
    return { kind: "quadratic", A, B, C };
  }

  // cubic: Ax^3 + Bx + C
  const A = randInt(-2, 2, true);
  const B = randInt(-6, 6, false);
  const C = randInt(-6, 6, false);
  return { kind: "cubic", A, B, C };
}

function baseFunctionLatex(base) {
  if (base.kind === "linear") {
    const { a, b } = base;
    if (b === 0) return `${latexAx(a)}`;
    return `${latexAx(a)} ${b < 0 ? "- " + Math.abs(b) : "+ " + b}`;
  }

  if (base.kind === "quadratic") {
    const { A, B, C } = base;
    return polyLatex({ a3: 0, a2: A, a1: B, a0: C });
  }

  const { A, B, C } = base;
  return polyLatex({ a3: A, a2: 0, a1: B, a0: C });
}

/* =========================
   Symmetry logic
   - xAxis:    g(x) = -f(x)
   - yAxis:    g(x) = f(-x)
   - origin:   g(x) = -f(-x)
========================= */

function symmetryPick() {
  const r = Math.random();
  if (r < 1 / 3) return "xAxis";
  if (r < 2 / 3) return "yAxis";
  return "origin";
}

function ruleLatex(sym) {
  if (sym === "xAxis") return "g(x)=-f(x)";
  if (sym === "yAxis") return "g(x)=f(-x)";
  return "g(x)=-f(-x)";
}

function ruleText(sym) {
  if (sym === "xAxis") return "Symetria względem osi OX: wartości funkcji zmieniają znak (y → -y).";
  if (sym === "yAxis") return "Symetria względem osi OY: w argumencie podstawiamy x → -x.";
  return "Symetria względem środka układu: łączymy oba kroki (x → -x oraz y → -y).";
}

function axisInline(sym) {
  if (sym === "xAxis") return <InlineMath math={"OX"} />;
  if (sym === "yAxis") return <InlineMath math={"OY"} />;
  return <>środka układu współrzędnych</>;
}

function symmetryPhrase(sym) {
  if (sym === "origin") return <>symetrię względem środka układu współrzędnych</>;
  return (
    <>
      symetrię względem osi {axisInline(sym)}
    </>
  );
}

function expandedAnswerLatex(base, sym) {
  // Zwraca WYNIK w postaci wielomianu/wyrażenia w x (po przekształceniu).
  if (base.kind === "linear") {
    const { a, b } = base;

    if (sym === "xAxis") {
      // -(ax + b) = -ax - b
      const aNew = -a;
      const bNew = -b;
      if (bNew === 0) return `${latexAx(aNew)}`;
      return `${latexAx(aNew)} ${bNew < 0 ? "- " + Math.abs(bNew) : "+ " + bNew}`;
    }
    if (sym === "yAxis") {
      // f(-x)=a(-x)+b = -a x + b
      const aNew = -a;
      const bNew = b;
      if (bNew === 0) return `${latexAx(aNew)}`;
      return `${latexAx(aNew)} ${bNew < 0 ? "- " + Math.abs(bNew) : "+ " + bNew}`;
    }
    // origin: -f(-x) = -(-ax + b) = a x - b
    const aNew = a;
    const bNew = -b;
    if (bNew === 0) return `${latexAx(aNew)}`;
    return `${latexAx(aNew)} ${bNew < 0 ? "- " + Math.abs(bNew) : "+ " + bNew}`;
  }

  if (base.kind === "quadratic") {
    const { A, B, C } = base;

    if (sym === "xAxis") {
      // -(Ax^2 + Bx + C) = -Ax^2 -Bx -C
      return polyLatex({ a3: 0, a2: -A, a1: -B, a0: -C });
    }
    if (sym === "yAxis") {
      // f(-x)=Ax^2 -Bx + C
      return polyLatex({ a3: 0, a2: A, a1: -B, a0: C });
    }
    // origin: -f(-x)=-(Ax^2 -Bx + C)= -Ax^2 +Bx -C
    return polyLatex({ a3: 0, a2: -A, a1: B, a0: -C });
  }

  // cubic: Ax^3 + Bx + C
  const { A, B, C } = base;

  if (sym === "xAxis") {
    // -(Ax^3 + Bx + C) = -Ax^3 -Bx -C
    return polyLatex({ a3: -A, a2: 0, a1: -B, a0: -C });
  }
  if (sym === "yAxis") {
    // f(-x)= -Ax^3 -Bx + C
    return polyLatex({ a3: -A, a2: 0, a1: -B, a0: C });
  }
  // origin: -f(-x)= -(-Ax^3 -Bx + C)= Ax^3 + Bx - C
  return polyLatex({ a3: A, a2: 0, a1: B, a0: -C });
}

function acceptedNormalizedForms(base, sym) {
  const forms = [];

  const fLatex = baseFunctionLatex(base);
  const expanded = expandedAnswerLatex(base, sym);

  // 1) forma rozwinięta (z renderAnswer)
  forms.push(normalizeExpr(expanded));

  // 2) forma „regułowa” z podstawieniem (bardziej tolerancyjna)
  // xAxis: - (f(x))
  // yAxis: f(-x)
  // origin: -(f(-x))
  if (sym === "xAxis") {
    forms.push(normalizeExpr(`-(${fLatex})`));
    forms.push(normalizeExpr(`-( ${fLatex} )`));
  } else if (sym === "yAxis") {
    // tu nie mamy symbolu f, tylko bezpośrednie podstawienie:
    // podmieniamy x -> (-x) w latex bazowym "na miękko" (w praktyce uczniowie i tak wpiszą wynik)
    // Dodajemy też wariant z nawiasem: (...) i bez
    forms.push(normalizeExpr(fLatex.replace(/x/g, "(-x)")));
    forms.push(normalizeExpr(fLatex.replace(/x/g, "-x")));
  } else {
    forms.push(normalizeExpr(`-(${fLatex.replace(/x/g, "(-x)")})`));
    forms.push(normalizeExpr(`-(${fLatex.replace(/x/g, "-x")})`));
  }

  return Array.from(new Set(forms));
}

/* =========================
   Graph expressions
========================= */

function graphExprForBase(base) {
  if (base.kind === "linear") {
    const { a, b } = base;
    const bPart = b === 0 ? "" : b > 0 ? ` + ${b}` : ` - ${Math.abs(b)}`;
    return `y = ${exprAx(a)}${bPart}`;
  }
  if (base.kind === "quadratic") {
    const { A, B, C } = base;
    const Bpart = B === 0 ? "" : B > 0 ? ` + ${B} * x` : ` - ${Math.abs(B)} * x`;
    const Cpart = C === 0 ? "" : C > 0 ? ` + ${C}` : ` - ${Math.abs(C)}`;
    return `y = ${exprAxx(A)}${Bpart}${Cpart}`;
  }
  const { A, B, C } = base;
  const Bpart = B === 0 ? "" : B > 0 ? ` + ${B} * x` : ` - ${Math.abs(B)} * x`;
  const Cpart = C === 0 ? "" : C > 0 ? ` + ${C}` : ` - ${Math.abs(C)}`;
  return `y = ${exprAxxx(A)}${Bpart}${Cpart}`;
}

function graphExprForSymmetry(base, sym) {
  // Zwraca eq "y = ..." po symetrii (używamy współczynników jak w expandedAnswerLatex)
  const latex = expandedAnswerLatex(base, sym);

  // Konwersja latex -> prosty JS dla GraphViewer:
  // - usuwamy spacje
  // - zamieniamy x^3, x^2
  // - dodajemy mnożenia w stylu "3x" -> "3 * x" (minimalnie)
  // Żeby było stabilne, zrobimy współczynniki bez parsowania latexu:
  if (base.kind === "linear") {
    let a, b;
    if (sym === "xAxis") {
      a = -base.a;
      b = -base.b;
    } else if (sym === "yAxis") {
      a = -base.a;
      b = base.b;
    } else {
      a = base.a;
      b = -base.b;
    }
    const bPart = b === 0 ? "" : b > 0 ? ` + ${b}` : ` - ${Math.abs(b)}`;
    return `y = ${exprAx(a)}${bPart}`;
  }

  if (base.kind === "quadratic") {
    let A, B, C;
    if (sym === "xAxis") {
      A = -base.A;
      B = -base.B;
      C = -base.C;
    } else if (sym === "yAxis") {
      A = base.A;
      B = -base.B;
      C = base.C;
    } else {
      A = -base.A;
      B = base.B;
      C = -base.C;
    }
    const Bpart = B === 0 ? "" : B > 0 ? ` + ${B} * x` : ` - ${Math.abs(B)} * x`;
    const Cpart = C === 0 ? "" : C > 0 ? ` + ${C}` : ` - ${Math.abs(C)}`;
    return `y = ${exprAxx(A)}${Bpart}${Cpart}`;
  }

  // cubic
  let A, B, C;
  if (sym === "xAxis") {
    A = -base.A;
    B = -base.B;
    C = -base.C;
  } else if (sym === "yAxis") {
    A = -base.A;
    B = -base.B;
    C = base.C;
  } else {
    A = base.A;
    B = base.B;
    C = -base.C;
  }
  const Bpart = B === 0 ? "" : B > 0 ? ` + ${B} * x` : ` - ${Math.abs(B)} * x`;
  const Cpart = C === 0 ? "" : C > 0 ? ` + ${C}` : ` - ${Math.abs(C)}`;
  return `y = ${exprAxxx(A)}${Bpart}${Cpart}`;
}

/* =========================
   Generator
========================= */

export function generateTask(forceSymmetry) {
  const symmetry = forceSymmetry || symmetryPick(); // xAxis | yAxis | origin
  const base = makeBaseFunction();

  const answerLatex = expandedAnswerLatex(base, symmetry);
  const accepted = acceptedNormalizedForms(base, symmetry);

  return {
    type: "SymmetryFunctionTask",
    data: {
      symmetry,
      base,
      answerLatex,
      accepted,
    },
  };
}

/* =========================
   Render task
========================= */

export function renderTask({ data }) {
  const { base, symmetry } = data;

  return (
    <div style={{ marginTop: 6 }}>
      <div>
        Dana jest funkcja:
        <BlockMath math={`f(x) = ${baseFunctionLatex(base)}`} />
      </div>

      <div style={{ marginTop: 6 }}>
        Wykres funkcji <InlineMath math={"f"} /> odbito przez <strong>{symmetryPhrase(symmetry)}</strong>.
      </div>

      <div style={{ marginTop: 10 }}>
        Podaj wzór funkcji <InlineMath math={"g"} /> w postaci{" "}
        <strong>uporządkowanego wielomianu</strong> (po wymnożeniu i redukcji).
      </div>
    </div>
  );
}

/* =========================
   Input + preview
========================= */

export function renderInput(_task, userInput = {}, onChange, disabled) {
  const ui = userInput && typeof userInput === "object" ? userInput : {};
  const value = ui.eq ?? "";

  const handleChange = (v) => {
    const base = userInput && typeof userInput === "object" ? userInput : {};
    onChange({ ...base, eq: v });
  };

  const raw = typeof value === "object" ? value?.raw ?? "" : String(value ?? "");
  const cleaned = stripLeadingAssignment(raw);
  const has = cleaned.trim().length > 0;

  return (
    <div className="flex flex-col gap-2" style={{ marginTop: 8 }}>
      <div className="flex items-center gap-2">
        <InlineMath math={"g(x)="} />
        <div style={{ minWidth: 260 }}>
          <EquationInput
            value={value}
            onChange={handleChange}
            disabled={disabled}
            showPreview={false}
            placeholder={"np. 2x^3-4x^2+3x-1"}
          />
        </div>
      </div>

      {has ? (
        <div style={{ marginTop: 2 }}>
          <strong>Podgląd:</strong> <InlineMath math={`g(x)=${cleaned}`} />
        </div>
      ) : null}
    </div>
  );
}


/* =========================
   Check
========================= */

export function checkAnswer(task, userInput = {}) {
  const accepted = task?.data?.accepted || [];
  const val = userInput?.eq;

  const got = normalizeExpr(val);
  if (!got) return false;

  return accepted.includes(got);
}

/* =========================
   Answer
========================= */

export function renderAnswer({ data }) {
  return <InlineMath math={`g(x)=${data.answerLatex}`} />;
}

/* =========================
   Explanation
========================= */

export function renderExplanation({ data }) {
  const { base, symmetry, answerLatex } = data;

  const eqF = graphExprForBase(base);
  const eqG = graphExprForSymmetry(base, symmetry);

  const what =
    symmetry === "xAxis"
      ? "osi OX"
      : symmetry === "yAxis"
      ? "osi OY"
      : "środka układu współrzędnych";

  // kroki:
  // 1) reguła
  // 2) podstawienie do f
  // 3) wynik
  const fLatex = baseFunctionLatex(base);

  let step2 = "";
  if (symmetry === "xAxis") step2 = `g(x)=-\\left(${fLatex}\\right)`;
  else if (symmetry === "yAxis") step2 = `g(x)=f(-x)`;
  else step2 = `g(x)=-f(-x)`;

  // dla yAxis/origin pokażemy podstawienie „na wyrażeniu”
  let step3 = "";
  if (symmetry === "yAxis") {
    step3 = `g(x)=${fLatex.replace(/x/g, "(-x)")}`;
  } else if (symmetry === "origin") {
    step3 = `g(x)=-\\left(${fLatex.replace(/x/g, "(-x)")}\\right)`;
  }

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>
        <strong>Krok 1.</strong> Zapisujemy regułę symetrii względem {what}.
      </div>
      <div>{ruleText(symmetry)}</div>
      <BlockMath math={ruleLatex(symmetry)} />

      <div style={{ marginTop: 8 }}>
        <strong>Krok 2.</strong> Podstawiamy do wzoru funkcji <InlineMath math={"f"} />:
      </div>
      <BlockMath math={step2} />

      {step3 ? (
        <>
          <div style={{ marginTop: 8 }}>
            <strong>Krok 3.</strong> Wykonujemy podstawienie i upraszczamy:
          </div>
          <BlockMath math={step3} />
        </>
      ) : null}

      <div style={{ marginTop: 8 }}>
        <strong>Wynik:</strong>
      </div>
      <BlockMath math={`g(x)=${answerLatex}`} />

      <div style={{ marginTop: 12 }}>
        <strong>Wizualizacja (przed i po odbiciu):</strong>
        <div style={{ display: "flex", alignItems: "center", gap: "1.5rem", flexWrap: "wrap" }}>
          <GraphViewer
            equations={[
              { expr: eqF, color: "purple", label: "f" },
              { expr: eqG, color: "orange", label: "g" },
            ]}
            xRange={[-8, 8]}
            yRange={[-8, 8]}
            showGrid
            width={420}
            height={420}
          />

          <div style={{ fontSize: "1rem", lineHeight: 1.6 }}>
            <div style={{ color: "purple" }}>
              <InlineMath math={`f(x)=${fLatex}`} />
            </div>
            <div style={{ color: "orange" }}>
              <InlineMath math={`g(x)=${answerLatex}`} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function renderUserAnswer(userInput = {}) {
  const val = userInput?.eq;
  const raw = typeof val === "object" ? val?.raw ?? "" : String(val ?? "");
  const cleaned = stripLeadingAssignment(raw);

  if (!cleaned.trim()) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={`g(x)=${cleaned.trim()}`} />;
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
